// Typed wrappers around /api/config — read returns the redacted view
// (api_key_present + api_key_hint, never the raw key). The Anthropic key is
// supplied via the deploy environment (ANTHROPIC_API_KEY), not written here;
// POST only patches non-secret prefs (e.g. claude_model).
import { z } from "zod";

const ClaudeModelOptionSchema = z.object({
  id: z.string(),
  label: z.string(),
  tier: z.string().nullable().optional(),
});

const ConfigResponseSchema = z.object({
  workspace: z.string().nullable().optional(),
  research_dir: z.string().nullable().optional(),
  setups_dir: z.string().nullable().optional(),
  default_segment: z.number().nullable().optional(),
  default_iterations: z.number().nullable().optional(),
  claude_model: z.string().nullable().optional(),
  api_key_present: z.boolean(),
  api_key_hint: z.string().optional().default(""),
  claude_model_options: z.array(ClaudeModelOptionSchema).optional().default([]),
});

export type ClaudeModelOption = z.infer<typeof ClaudeModelOptionSchema>;
export type ConfigResponse = z.infer<typeof ConfigResponseSchema>;

export type ConfigPatch = {
  claude_model?: string;
  default_segment?: number;
  default_iterations?: number;
};

export const CONFIG_CHANGED_EVENT = "damperai:config-changed";

export async function fetchConfig(): Promise<ConfigResponse> {
  const r = await fetch("/api/config");
  if (!r.ok) throw new Error(`GET /api/config failed: ${r.status}`);
  return ConfigResponseSchema.parse(await r.json());
}

export async function updateConfig(patch: ConfigPatch): Promise<ConfigResponse> {
  const r = await fetch("/api/config", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new Error(`POST /api/config failed: ${r.status} ${text}`);
  }
  const parsed = ConfigResponseSchema.parse(await r.json());
  window.dispatchEvent(new CustomEvent(CONFIG_CHANGED_EVENT));
  return parsed;
}
